import {useEffect, useRef, useState} from "react";
import {useFigure} from "../../hooks/useJsonLoader";
import {FigureType} from "../../types/enums";
import {ListFigureData} from "../../types/list";

const purgeContainer = (container: HTMLElement | null) => {
  if (container && (window as any).Plotly) {
    container.querySelectorAll('.js-plotly-plot').forEach(el => (window as any).Plotly.purge(el));
  }
};

const injectHtml = (container: HTMLElement, html: string) => {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  while (temp.firstChild) container.appendChild(temp.firstChild);
  container.querySelectorAll('script').forEach(old => {
    const s = document.createElement('script');
    Array.from(old.attributes).forEach(a => s.setAttribute(a.name, a.value));
    s.textContent = old.textContent;
    old.parentNode?.replaceChild(s, old);
  });
};

const GeneFigure = ({type, data, title, labelClass = ''}: { type: FigureType; data: ListFigureData; title?: string; labelClass?: string }) => {
  const figure = useFigure(type);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const requestIdRef = useRef(0);
  const loadedOnceRef = useRef(false);
  const dataKey = JSON.stringify(data);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const id = ++requestIdRef.current;
    setLoading(true);
    figure({...JSON.parse(dataKey) as ListFigureData}, false).then(result => {
      if (requestIdRef.current !== id || !result) return;
      purgeContainer(container);
      container.innerHTML = '';
      injectHtml(container, result.html);
      loadedOnceRef.current = true;
      setLoading(false);
    });
    return () => { requestIdRef.current += 1; };
  }, [figure, dataKey]);

  useEffect(() => {
    const container = containerRef.current;
    return () => purgeContainer(container);
  }, []);

  return (
    <>
      {title && <h6 className={`section-label ${labelClass}`}>{title}</h6>}
      {loading && !loadedOnceRef.current && (
        <div className="d-flex justify-content-center py-4"><div className="spinner-border" role="status"/></div>
      )}
      <div className="position-relative">
        {loading && loadedOnceRef.current && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center gene-figure-overlay">
            <div className="spinner-border" role="status"/>
          </div>
        )}
        <div className="w-100" ref={containerRef}/>
      </div>
    </>
  );
};

export default GeneFigure;
